/**
 * Utility functions for parsing and handling field placeholders in declaration text
 * Placeholders format: {{field:fieldId}}
 */

export interface PlaceholderMatch {
  /** Full placeholder text (e.g., "{{field:childName}}") */
  placeholder: string;
  /** Field ID (e.g., "childName") */
  fieldId: string;
  /** Start index in text */
  startIndex: number;
  /** End index in text */
  endIndex: number;
}

/**
 * Regular expression to match field placeholders
 * Format: {{field:fieldId}}
 * Supports Latin, Cyrillic, numbers, underscores, and hyphens
 */
const PLACEHOLDER_REGEX = /\{\{field:([\w\u0400-\u04FF_-]+)\}\}/g;

/**
 * Find all placeholders in text
 */
export function findPlaceholders(text: string): PlaceholderMatch[] {
  const matches: PlaceholderMatch[] = [];
  let match: RegExpExecArray | null;

  // Reset regex state
  PLACEHOLDER_REGEX.lastIndex = 0;

  while ((match = PLACEHOLDER_REGEX.exec(text)) !== null) {
    matches.push({
      placeholder: match[0],
      fieldId: match[1],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }

  return matches;
}

/**
 * Split text into segments (text and placeholders)
 */
export interface TextSegment {
  type: 'text' | 'placeholder';
  content: string;
  fieldId?: string; // Only for placeholder segments
}

export function splitTextByPlaceholders(text: string): TextSegment[] {
  const placeholders = findPlaceholders(text);

  if (placeholders.length === 0) {
    return [{ type: 'text', content: text }];
  }

  const segments: TextSegment[] = [];
  let currentIndex = 0;

  placeholders.forEach((placeholder) => {
    // Add text before placeholder
    if (placeholder.startIndex > currentIndex) {
      segments.push({
        type: 'text',
        content: text.substring(currentIndex, placeholder.startIndex),
      });
    }

    // Add placeholder segment
    segments.push({
      type: 'placeholder',
      content: placeholder.placeholder,
      fieldId: placeholder.fieldId,
    });

    currentIndex = placeholder.endIndex;
  });

  // Add remaining text after last placeholder
  if (currentIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(currentIndex),
    });
  }

  return segments;
}

/**
 * Format a field value for display
 * Handles different types: strings, arrays (tables), booleans, etc.
 */
function formatValueForDisplay(value: unknown, fieldId: string): string {
  if (value === undefined || value === null) {
    return `[${fieldId}]`;
  }

  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Да' : 'Не';
  }

  // Handle arrays (tables)
  if (Array.isArray(value)) {
    // Each row is an object with {id, values: {col1: val1, col2: val2}}
    if (value.length === 0) {
      return '(няма данни)';
    }

    // Format table rows as readable text
    const rows = value.map((row, index) => {
      if (row && typeof row === 'object' && 'values' in row) {
        const vals = Object.values(row.values as Record<string, string>);
        return `${index + 1}. ${vals.join(', ')}`;
      }
      return String(row);
    });

    return rows.join('\n');
  }

  // Handle objects (shouldn't happen, but just in case)
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  // Default: convert to string
  return String(value);
}

/**
 * Replace all placeholders in text with values
 * Used for View modal and PDF generation
 * Handles all field types including tables
 */
export function replacePlaceholders(
  text: string,
  values: Record<string, unknown>
): string {
  let result = text;

  const placeholders = findPlaceholders(text);

  // Replace in reverse order to maintain correct indices
  placeholders.reverse().forEach((placeholder) => {
    const value = values[placeholder.fieldId];
    const formatted = formatValueForDisplay(value, placeholder.fieldId);
    result =
      result.substring(0, placeholder.startIndex) +
      formatted +
      result.substring(placeholder.endIndex);
  });

  return result;
}

/**
 * Check if text contains any placeholders
 */
export function hasPlaceholders(text: string): boolean {
  return PLACEHOLDER_REGEX.test(text);
}

/**
 * Get all field IDs referenced in text
 */
export function getReferencedFieldIds(text: string): string[] {
  const placeholders = findPlaceholders(text);
  return placeholders.map((p) => p.fieldId);
}

/**
 * Validate that all placeholders reference existing fields
 */
export function validatePlaceholders(
  text: string,
  fieldIds: string[]
): { valid: boolean; invalidFieldIds: string[] } {
  const referencedIds = getReferencedFieldIds(text);
  const invalidFieldIds = referencedIds.filter((id) => !fieldIds.includes(id));

  return {
    valid: invalidFieldIds.length === 0,
    invalidFieldIds,
  };
}
